import { useState, useEffect, useRef, type CSSProperties } from "react";
import { CopyableCode } from "../primitives";

/**
 * Sprint 14g — Activity-specific delete confirmation. Activity is an audit
 * trail, so deleting one is heavier than other primitives: a free-text reason
 * (min 5 chars) is REQUIRED and captured into audit_log alongside a payload
 * snapshot. Mirrors ConfirmDeleteRowModal's type-the-name ceremony but adds the
 * reason textarea and audit-trail warning copy. No back-ref surface — an
 * Activity is a leaf.
 */
export interface ConfirmDeleteActivityModalProps {
  open: boolean;
  onClose: () => void;
  /** The activity subject — typed to confirm. Falls back to the id when blank. */
  subject: string;
  activityId: string;
  onConfirm: (opts: { reason: string }) => Promise<void>;
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 200,
  fontFamily: "var(--font-sans)",
};
const modal: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-secondary)",
  borderRadius: "var(--border-radius-lg)",
  padding: 24,
  width: 460,
  maxWidth: "90vw",
};
const fieldStyle: CSSProperties = {
  width: "100%",
  fontSize: 13,
  padding: "8px 10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  boxSizing: "border-box",
};
const btnBase: CSSProperties = {
  fontSize: 13,
  padding: "8px 16px",
  borderRadius: "var(--border-radius-md)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  fontWeight: 500,
  border: "none",
};

const MIN_REASON = 5;
const MAX_REASON = 500;

export function ConfirmDeleteActivityModal({
  open,
  onClose,
  subject,
  activityId,
  onConfirm,
}: ConfirmDeleteActivityModalProps) {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setTyped("");
      setReason("");
      setError(null);
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const target = subject.trim() || activityId;
  const reasonOk = reason.trim().length >= MIN_REASON;
  const confirmed = typed.trim() === target && reasonOk;

  async function handleDelete() {
    if (!confirmed || busy) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm({ reason: reason.trim() });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
      setBusy(false);
    }
  }

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Delete activity" style={modal}>
        <h2 style={{ fontSize: 18, fontWeight: 500, margin: "0 0 8px 0", color: "var(--color-text-primary)" }}>
          Delete this activity?
        </h2>
        <div style={{ fontSize: 14, color: "var(--color-text-secondary)", lineHeight: 1.5, marginBottom: 16 }}>
          Deleting an activity removes a record from your audit trail. Provide a reason — it is recorded in{" "}
          <strong style={{ color: "var(--color-text-primary)" }}>audit_log</strong> along with a snapshot for
          recovery. This cannot be undone; use archive instead if you only want it hidden.
        </div>

        <label style={{ display: "block", fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 6 }}>
          Reason <span style={{ color: "var(--color-text-tertiary)" }}>(min {MIN_REASON} characters)</span>
        </label>
        <textarea
          style={{ ...fieldStyle, minHeight: 64, resize: "vertical", marginBottom: 14 }}
          value={reason}
          maxLength={MAX_REASON}
          onChange={(e) => { setReason(e.target.value); if (error) setError(null); }}
          placeholder="Why is this activity being deleted?"
          aria-label="Deletion reason"
        />

        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4, fontSize: 12, color: "var(--color-text-secondary)", marginBottom: 6 }}>
          Type <CopyableCode text={target} label="Copy name" /> to confirm
        </div>
        <input
          ref={inputRef}
          type="text"
          style={fieldStyle}
          value={typed}
          onChange={(e) => { setTyped(e.target.value); if (error) setError(null); }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && confirmed) handleDelete();
            if (e.key === "Escape" && !busy) onClose();
          }}
          placeholder={target}
          aria-label="Activity confirmation"
        />

        {error && <div style={{ fontSize: 13, color: "var(--c-red-600)", marginTop: 12 }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{ ...btnBase, background: "transparent", color: "var(--color-text-secondary)" }}
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={!confirmed || busy}
            style={{
              ...btnBase,
              background: "var(--c-red-600)",
              color: "#fff",
              opacity: !confirmed || busy ? 0.5 : 1,
              cursor: !confirmed || busy ? "not-allowed" : "pointer",
            }}
          >
            {busy ? "Deleting…" : "Delete activity"}
          </button>
        </div>
      </div>
    </div>
  );
}
